import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { FaCopy, FaPenToSquare } from 'react-icons/fa6';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';

import Header from '../common/header.js';
import { ApiError } from '../common/apiError.js';
import { userAppData } from '../MainApp.js';
import {
    getBookingTimetableMasters,
    saveBookingTimetableMaster,
} from './sysManagerBookingTimetablesApi.js';

import './SysManagerBookingTimetables.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const DAYS = [
    { code: 'MON', label: 'Monday' },
    { code: 'TUE', label: 'Tuesday' },
    { code: 'WED', label: 'Wednesday' },
    { code: 'THU', label: 'Thursday' },
    { code: 'FRI', label: 'Friday' },
];
const SLOT_STATES = [
    { value: 'FREE', label: 'Free' },
    { value: 'TIMETABLED', label: 'Timetabled' },
    { value: 'UNAVAILABLE', label: 'Unavailable' },
];

function hasBookingsAdminAccess() {
    const sysManagerPerms = userAppData?.sysManager || userAppData?.sysManagerData || {};
    return sysManagerPerms.perms === 1 && sysManagerPerms.bookingsAdmin === true;
}

function blankSlot() {
    return { state: 'FREE', allocation: '', staffCogId: '', notes: '' };
}

function buildBlankWeek(lessonNames) {
    return DAYS.reduce((week, day) => {
        week[day.code] = lessonNames.reduce((slots, lessonName) => {
            slots[lessonName] = blankSlot();
            return slots;
        }, {});
        return week;
    }, {});
}

function buildBlankWeeks(lessonNames, cycleLength) {
    const weeks = {};
    for (let weekNumber = 1; weekNumber <= cycleLength; weekNumber += 1) {
        weeks[String(weekNumber)] = buildBlankWeek(lessonNames);
    }
    return weeks;
}

function normaliseWeeks(rawWeeks, lessonNames, cycleLength) {
    const weeks = buildBlankWeeks(lessonNames, cycleLength);
    for (let weekNumber = 1; weekNumber <= cycleLength; weekNumber += 1) {
        const weekKey = String(weekNumber);
        DAYS.forEach((day) => {
            lessonNames.forEach((lessonName) => {
                const storedSlot = rawWeeks?.[weekKey]?.[day.code]?.[lessonName];
                if (storedSlot && typeof storedSlot === 'object') {
                    weeks[weekKey][day.code][lessonName] = {
                        ...blankSlot(),
                        ...storedSlot,
                    };
                }
            });
        });
    }
    return weeks;
}

function emptyEditorState() {
    return {
        roomId: '',
        name: '',
        cycleLength: 1,
        status: 'DRAFT',
        revision: 0,
        weeks: {},
    };
}

function slotDisplay(slot) {
    if (slot?.state === 'TIMETABLED') {
        return slot.allocation || 'Timetabled';
    }
    return slot?.state === 'UNAVAILABLE' ? 'Unavailable' : 'Free';
}

export default function SysManagerBookingTimetables() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [bootstrap, setBootstrap] = useState({
        academicYear: '', totalWeeksInCycle: 2, rooms: [], groups: [], lessonNames: [], staff: [], masters: [],
    });
    const [editor, setEditor] = useState(emptyEditorState);
    const [activeWeek, setActiveWeek] = useState(1);
    const [cellEditor, setCellEditor] = useState(null);

    const groupNames = useMemo(() => (
        (bootstrap.groups || []).reduce((lookup, group) => {
            lookup[group.groupId] = group.name;
            return lookup;
        }, {})
    ), [bootstrap.groups]);

    const staffOptions = useMemo(() => (
        (bootstrap.staff || []).map((staffMember) => ({
            value: staffMember.cogId,
            label: staffMember.displayName,
        }))
    ), [bootstrap.staff]);

    const staffNames = useMemo(() => (
        (bootstrap.staff || []).reduce((lookup, staffMember) => {
            lookup[staffMember.cogId] = staffMember.displayName;
            return lookup;
        }, {})
    ), [bootstrap.staff]);

    useEffect(() => {
        if (!hasBookingsAdminAccess()) {
            navigate('/app/sysmanager');
            return;
        }

        async function loadPage() {
            setLoading(true);
            setApiError(false);
            try {
                const response = await getBookingTimetableMasters();
                setBootstrap(response);
            } catch (error) {
                console.error('Failed to load booking timetable masters', error);
                setApiError(true);
            } finally {
                setLoading(false);
            }
        }

        loadPage();
    }, [navigate]);

    function loadRoomMaster(roomId) {
        const room = bootstrap.rooms.find((item) => item.roomId === roomId);
        const existingMaster = bootstrap.masters.find((item) => item.roomId === roomId);
        const cycleLength = Number(existingMaster?.cycleLength || 1);
        setEditor({
            roomId,
            name: existingMaster?.name || `${room?.name || 'Room'} timetable`,
            cycleLength,
            status: existingMaster?.status || 'DRAFT',
            revision: Number(existingMaster?.revision || 0),
            weeks: normaliseWeeks(existingMaster?.weeks, bootstrap.lessonNames, cycleLength),
        });
        setActiveWeek(1);
        setCellEditor(null);
    }

    function changeCycleLength(nextCycleLength) {
        const cycleLength = Number(nextCycleLength);
        setEditor((current) => ({
            ...current,
            cycleLength,
            weeks: normaliseWeeks(current.weeks, bootstrap.lessonNames, cycleLength),
        }));
        setActiveWeek((current) => Math.min(current, cycleLength));
    }

    function openCellEditor(dayCode, lessonName) {
        setCellEditor({
            dayCode,
            lessonName,
            slot: {
                ...blankSlot(),
                ...editor.weeks?.[String(activeWeek)]?.[dayCode]?.[lessonName],
            },
        });
    }

    function saveCell() {
        if (!cellEditor) return;
        const cleanAllocation = String(cellEditor.slot.allocation || '').trim();
        if (cellEditor.slot.state === 'TIMETABLED' && !cleanAllocation) {
            toast.error('❌ Enter a class or allocation for a timetabled slot.', TOAST_CONFIG);
            return;
        }

        setEditor((current) => {
            const weeks = normaliseWeeks(current.weeks, bootstrap.lessonNames, current.cycleLength);
            weeks[String(activeWeek)][cellEditor.dayCode][cellEditor.lessonName] = {
                state: cellEditor.slot.state,
                allocation: cellEditor.slot.state === 'TIMETABLED' ? cleanAllocation : '',
                staffCogId: cellEditor.slot.state === 'TIMETABLED'
                    ? String(cellEditor.slot.staffCogId || '').trim()
                    : '',
                notes: String(cellEditor.slot.notes || '').trim(),
            };
            return { ...current, weeks };
        });
        setCellEditor(null);
    }

    function copyWeekOne() {
        if (editor.cycleLength < 2) return;
        const sourceWeek = editor.weeks?.['1'];
        const weeks = { ...editor.weeks };
        for (let weekNumber = 2; weekNumber <= editor.cycleLength; weekNumber += 1) {
            weeks[String(weekNumber)] = JSON.parse(JSON.stringify(sourceWeek));
        }
        setEditor((current) => ({ ...current, weeks }));
        toast.success('✅ Week 1 copied to the other timetable weeks.', TOAST_CONFIG);
    }

    async function handleSave() {
        if (!editor.roomId || !String(editor.name || '').trim()) {
            toast.error('❌ Select a room and enter a timetable name.', TOAST_CONFIG);
            return;
        }

        setSaving(true);
        try {
            const response = await saveBookingTimetableMaster({
                academicYear: bootstrap.academicYear,
                roomId: editor.roomId,
                name: String(editor.name).trim(),
                cycleLength: editor.cycleLength,
                status: editor.status,
                revision: editor.revision,
                weeks: editor.weeks,
            });
            const savedMaster = response.master;
            setBootstrap((current) => ({
                ...current,
                masters: [
                    ...current.masters.filter((item) => item.roomId !== savedMaster.roomId),
                    savedMaster,
                ],
            }));
            setEditor((current) => ({ ...current, revision: Number(savedMaster.revision) }));
            toast.success('✅ Timetable master saved.', TOAST_CONFIG);
        } catch (error) {
            console.error('Failed to save booking timetable master', error);
            toast.error(`❌ ${error?.message || 'Unable to save timetable master.'}`, TOAST_CONFIG);
        } finally {
            setSaving(false);
        }
    }

    if (apiError) return <ApiError />;

    return (
        <div className='main_div'>
            <div className='internal_main_div sysManager_bookingTimetables_container'>
                <Header
                    title='Booking Timetable Masters'
                    subtitle='Create the weekly room templates used by timetable bookings.'
                    rightContent={(
                        <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager/bookings')}>
                            <FaArrowLeft aria-hidden='true' />
                            Back to Bookings
                        </button>
                    )}
                />

                {loading ? (
                    <div className='sysManager_bookingTimetables_loading'>
                        <CgSpinner className='sysManager_bookingTimetables_spinner' />
                        <p>Loading timetable masters...</p>
                    </div>
                ) : (
                    <>
                        <section className='sysManager_bookingTimetables_card'>
                            <div className='sysManager_bookingTimetables_formGrid'>
                                <label>
                                    <span>Academic year</span>
                                    <input value={bootstrap.academicYear} disabled />
                                </label>
                                <label>
                                    <span>Booking room</span>
                                    <select value={editor.roomId} onChange={(event) => loadRoomMaster(event.target.value)}>
                                        <option value=''>Select a room</option>
                                        {bootstrap.rooms.map((room) => (
                                            <option value={room.roomId} key={room.roomId}>
                                                {room.name} ({groupNames[room.groupId] || 'Unknown group'})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    <span>Timetable name</span>
                                    <input
                                        value={editor.name}
                                        disabled={!editor.roomId}
                                        onChange={(event) => setEditor((current) => ({ ...current, name: event.target.value }))}
                                    />
                                </label>
                                <label>
                                    <span>Timetable cycle</span>
                                    <select
                                        value={editor.cycleLength}
                                        disabled={!editor.roomId}
                                        onChange={(event) => changeCycleLength(event.target.value)}
                                    >
                                        <option value={1}>Single week</option>
                                        {bootstrap.totalWeeksInCycle > 1 && (
                                            <option value={bootstrap.totalWeeksInCycle}>
                                                {bootstrap.totalWeeksInCycle}-week timetable
                                            </option>
                                        )}
                                    </select>
                                </label>
                                <label>
                                    <span>Status</span>
                                    <select
                                        value={editor.status}
                                        disabled={!editor.roomId}
                                        onChange={(event) => setEditor((current) => ({ ...current, status: event.target.value }))}
                                    >
                                        <option value='DRAFT'>Draft</option>
                                        <option value='ACTIVE'>Active</option>
                                    </select>
                                </label>
                            </div>
                        </section>

                        {editor.roomId && (
                            <section className='sysManager_bookingTimetables_card'>
                                <div className='sysManager_bookingTimetables_toolbar'>
                                    <div className='sysManager_bookingTimetables_weekTabs'>
                                        {Array.from({ length: editor.cycleLength }, (_, index) => index + 1).map((weekNumber) => (
                                            <button
                                                className={activeWeek === weekNumber ? 'is-active' : ''}
                                                type='button'
                                                key={weekNumber}
                                                onClick={() => setActiveWeek(weekNumber)}
                                            >
                                                Week {weekNumber}
                                            </button>
                                        ))}
                                    </div>
                                    {editor.cycleLength > 1 && (
                                        <button className='sysManager_bookingTimetables_secondaryButton' type='button' onClick={copyWeekOne}>
                                            <FaCopy aria-hidden='true' /> Copy Week 1 to all
                                        </button>
                                    )}
                                </div>

                                <div className='sysManager_bookingTimetables_tableWrap'>
                                    <table className='sysManager_bookingTimetables_table'>
                                        <thead>
                                            <tr>
                                                <th>Lesson</th>
                                                {DAYS.map((day) => <th key={day.code}>{day.label}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bootstrap.lessonNames.map((lessonName) => (
                                                <tr key={lessonName}>
                                                    <th>{lessonName}</th>
                                                    {DAYS.map((day) => {
                                                        const slot = editor.weeks?.[String(activeWeek)]?.[day.code]?.[lessonName] || blankSlot();
                                                        return (
                                                            <td key={`${day.code}-${lessonName}`}>
                                                                <button
                                                                    className={`sysManager_bookingTimetables_slot is-${String(slot.state).toLowerCase()}`}
                                                                    type='button'
                                                                    onClick={() => openCellEditor(day.code, lessonName)}
                                                                >
                                                                    <span>{slotDisplay(slot)}</span>
                                                                    {slot.staffCogId && (
                                                                        <small>{staffNames[slot.staffCogId] || 'Inactive staff member'}</small>
                                                                    )}
                                                                    <FaPenToSquare aria-hidden='true' />
                                                                </button>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className='sysManager_bookingTimetables_saveBar'>
                                    <span>Revision {editor.revision}</span>
                                    <button type='button' disabled={saving} onClick={handleSave}>
                                        {saving ? <CgSpinner className='sysManager_bookingTimetables_spinner' /> : 'Save timetable master'}
                                    </button>
                                </div>
                            </section>
                        )}
                    </>
                )}

                {cellEditor && (
                    <div className='sysManager_bookingTimetables_modalOverlay' role='presentation' onClick={() => setCellEditor(null)}>
                        <section className='sysManager_bookingTimetables_modal' role='dialog' aria-modal='true' onClick={(event) => event.stopPropagation()}>
                            <button className='sysManager_bookingTimetables_modalClose' type='button' onClick={() => setCellEditor(null)}>×</button>
                            <header>
                                <h2>{cellEditor.lessonName}</h2>
                                <p>{DAYS.find((day) => day.code === cellEditor.dayCode)?.label}, Week {activeWeek}</p>
                            </header>
                            <div className='sysManager_bookingTimetables_modalBody'>
                                <label>
                                    <span>Slot state</span>
                                    <select
                                        value={cellEditor.slot.state}
                                        onChange={(event) => setCellEditor((current) => ({
                                            ...current, slot: { ...current.slot, state: event.target.value },
                                        }))}
                                    >
                                        {SLOT_STATES.map((state) => <option value={state.value} key={state.value}>{state.label}</option>)}
                                    </select>
                                </label>
                                {cellEditor.slot.state === 'TIMETABLED' && (
                                    <>
                                        <label>
                                            <span>Class or allocation</span>
                                            <input
                                                value={cellEditor.slot.allocation}
                                                onChange={(event) => setCellEditor((current) => ({
                                                    ...current, slot: { ...current.slot, allocation: event.target.value },
                                                }))}
                                            />
                                        </label>
                                        <label>
                                            <span>Staff member (optional)</span>
                                            <Select
                                                className='sysManager_bookingTimetables_staffSelect'
                                                classNamePrefix='sysManager-booking-timetable-staff'
                                                options={staffOptions}
                                                value={staffOptions.find((option) => option.value === cellEditor.slot.staffCogId) || null}
                                                onChange={(selectedOption) => setCellEditor((current) => ({
                                                    ...current,
                                                    slot: { ...current.slot, staffCogId: selectedOption?.value || '' },
                                                }))}
                                                isClearable
                                                placeholder='Select a staff member...'
                                                noOptionsMessage={() => 'No active staff found'}
                                                menuPortalTarget={document.body}
                                                menuPosition='fixed'
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 1200 }) }}
                                            />
                                        </label>
                                    </>
                                )}
                                <label>
                                    <span>Notes (optional)</span>
                                    <textarea
                                        value={cellEditor.slot.notes}
                                        onChange={(event) => setCellEditor((current) => ({
                                            ...current, slot: { ...current.slot, notes: event.target.value },
                                        }))}
                                    />
                                </label>
                            </div>
                            <footer>
                                <button type='button' className='secondary' onClick={() => setCellEditor(null)}>Cancel</button>
                                <button type='button' className='primary' onClick={saveCell}>Save slot</button>
                            </footer>
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
}
